/**
 * Разбор ввода ручной операции.
 *
 * Сумма вводится в сомах — так её называют в разговоре и пишут в
 * платёжке, — а на бэк уходит в целых тыйынах.
 */

import type {LedgerKind} from '@services/ledger';

/** Направление — двумя явными кнопками, а не знаком в сумме. */
export const ENTRY_KINDS: {key: LedgerKind; label: string; hint: string}[] = [
  {key: 'credit', label: 'Пополнить', hint: 'деньги пришли'},
  {key: 'debit', label: 'Списать', hint: 'деньги ушли'},
];

/**
 * Потолок одной операции — десять миллионов сом.
 *
 * Не потому, что столько не бывает, а потому, что на счёте клиники
 * такая сумма почти наверняка опечатка в разряде. Бэк сверху не
 * ограничивает вовсе, так что единственная защита от лишнего нуля —
 * здесь.
 */
const MAX_TIYIN = 10_000_000 * 100;

/** Цифры, один разделитель, не больше двух знаков после него. */
const DECIMAL = /^\d+(?:[.,]\d{0,2})?$/;

/**
 * Сомы из поля → целые тыйыны. Пробелы между разрядами — нормальный
 * ввод человека, запятая вместо точки тоже.
 *
 * Разбираем регуляркой, а не одним `Number`: тот принимает `1e3`,
 * `0x10` и `Infinity`, и «1e3» молча превратилось бы в тысячу сом.
 * Всё, что не сложилось, — ноль: кнопка заперта, ругаться рано.
 */
export const toTiyin = (input: string): number => {
  const cleaned = input.replace(/\s/g, '');
  if (!DECIMAL.test(cleaned)) return 0;
  const tiyin = Math.round(Number(cleaned.replace(',', '.')) * 100);
  if (!Number.isSafeInteger(tiyin) || tiyin <= 0 || tiyin > MAX_TIYIN) return 0;
  return tiyin;
};

/** Каким станет остаток — показываем до сохранения, не после. */
export const nextBalanceTiyin = (balanceTiyin: number, kind: LedgerKind, amountTiyin: number) =>
  balanceTiyin + (kind === 'credit' ? amountTiyin : -amountTiyin);
